import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import moment from 'moment'

import { Avatar } from '../Avatar'
import { BackendRemote } from '../../backend-com'
import { selectedAccountId } from '../../ScreenController'
import { useRpcFetch } from '../../hooks/useFetch'

import styles from './styles.module.scss'

import type { T } from '@deltachat/jsonrpc-client'

/** A minimal contact shape — everything the {@link Avatar} and the name need. */
type ContactLike = Pick<
  T.Contact,
  'displayName' | 'profileImage' | 'address' | 'color'
>

export type PersonRow = {
  key: React.Key
  contact: ContactLike
  /** Trailing detail, e.g. a relative timestamp or a reaction emoji. */
  detail?: React.ReactNode
  /** `title` attribute for the detail (e.g. the absolute timestamp). */
  detailTitle?: string
}

/**
 * The floating card shown on hover: an optional title and a list of people,
 * each with their avatar, name and an optional trailing detail.
 */
export function PeopleHoverCard(props: {
  title?: React.ReactNode
  rows: PersonRow[]
  loading?: boolean
}) {
  const { title, rows, loading } = props

  return (
    <div className={styles.peopleHoverCard} role='tooltip'>
      {title != null && <div className={styles.peopleHoverTitle}>{title}</div>}
      {rows.length > 0 ? (
        // Plain `<div>`s with list/listitem roles instead of real `<ul>`/`<li>`
        // elements: this card can render anywhere in the app — including
        // inside the message list's own `<ol>` — and upstream's `ol li`
        // styling (margins, min-width, a clearfix) would otherwise bleed onto
        // genuine `<li>`s no matter how we scope our own class.
        <div className={styles.peopleHoverList} role='list'>
          {rows.map(row => (
            <div
              key={row.key}
              className={styles.peopleHoverRow}
              role='listitem'
            >
              <Avatar
                xSmall
                displayName={row.contact.displayName}
                avatarPath={row.contact.profileImage}
                addr={row.contact.address}
                color={row.contact.color}
                aria-hidden={true}
              />
              <span className={styles.peopleHoverName}>
                {row.contact.displayName}
              </span>
              {row.detail != null && (
                <span
                  className={styles.peopleHoverDetail}
                  title={row.detailTitle}
                >
                  {row.detail}
                </span>
              )}
            </div>
          ))}
        </div>
      ) : loading ? (
        <div className={styles.peopleHoverEmpty}>…</div>
      ) : null}
    </div>
  )
}

/**
 * Wraps a trigger element and reveals `card` in a popover while the pointer
 * hovers it (or it holds keyboard focus). `card` is only mounted while open,
 * so any data fetching inside it happens lazily on first hover.
 */
export function HoverInfoTrigger(props: {
  children: React.ReactNode
  card: React.ReactNode
  className?: string
  /** Horizontal edge the popover aligns to. Defaults to `end` (right in LTR). */
  align?: 'start' | 'end'
  /** Optional click handler forwarded to the trigger wrapper. */
  onClick?: () => void
}) {
  const [open, setOpen] = useState(false)
  const { align = 'end' } = props
  const anchorRef = useRef<HTMLSpanElement>(null)

  // Nudge the popover back into the viewport when it would overflow the left or
  // right edge — e.g. the "+N" reactions pill of a right-aligned outgoing
  // message, whose start-aligned popover would otherwise run off-screen. Done
  // imperatively (measuring in untransformed space) and re-run via a
  // ResizeObserver so it stays correct as the card grows when contacts load.
  useLayoutEffect(() => {
    const el = anchorRef.current
    if (!open || !el) {
      return
    }
    const clamp = () => {
      el.style.transform = 'none'
      const rect = el.getBoundingClientRect()
      const margin = 8
      let shift = 0
      if (rect.right > window.innerWidth - margin) {
        shift = window.innerWidth - margin - rect.right
      } else if (rect.left < margin) {
        shift = margin - rect.left
      }
      el.style.transform = shift !== 0 ? `translateX(${shift}px)` : ''
    }
    clamp()
    const observer = new ResizeObserver(clamp)
    observer.observe(el)
    return () => observer.disconnect()
  }, [open])

  return (
    <span
      className={classNames(styles.hoverInfoTrigger, props.className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onClick={props.onClick}
    >
      {props.children}
      {open && (
        <span
          ref={anchorRef}
          className={classNames(styles.hoverInfoAnchor, {
            [styles.anchorStart]: align === 'start',
            [styles.anchorEnd]: align === 'end',
          })}
        >
          {props.card}
        </span>
      )}
    </span>
  )
}

/**
 * Fetches the given contacts by id, keyed by id. Returns `{}` while loading or
 * when `contactIds` is empty. Memoises on the id list so a fresh array on every
 * render doesn't retrigger the fetch.
 */
export function useContactsById(
  contactIds: number[]
): Record<number, T.Contact> {
  const accountId = selectedAccountId()
  const idsKey = contactIds.join(',')
  // Stabilise the array reference so `useRpcFetch` only refetches when the
  // actual set of ids changes, not on every render.
  const stableIds = useMemo(() => contactIds, [idsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetch = useRpcFetch(
    BackendRemote.rpc.getContactsByIds,
    stableIds.length > 0 ? [accountId, stableIds] : null
  )

  return fetch?.lingeringResult?.ok ? fetch.lingeringResult.value : {}
}

/** Renders a relative timestamp like "5 minutes ago" from a unix-seconds value. */
export function RelativeTime(props: { timestamp: number }) {
  return <>{moment(props.timestamp * 1000).fromNow()}</>
}
